/**
 * components/instructor/QueueCardStack.jsx
 * ==========================================
 * Drag-and-drop queue card list using @hello-pangea/dnd.
 * Each card shows the student's position, name, pronouns (when the student has
 * set them), wait time, a Call button, and a Message button.
 *
 * Pronouns arrive on the instructor-view payload only, never on the SSE
 * broadcast, so they render for the instructor and for nobody else. Entries
 * merged from a live broadcast simply won't carry the field.
 */

import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import styles from "./QueueCardStack.module.css";

function waitTime(joinedAt) {
  const diff = Math.floor((Date.now() - new Date(joinedAt)) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function MessageModal({ entry, onSend, onClose }) {
  const [text, setText] = useState("");

  const handleSend = () => {
    if (!text.trim()) return;
    onSend(entry.entry_id, entry.student_id, text.trim());
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 24,
          width: 360,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 600, color: "var(--txt)", fontSize: 14 }}>
            Message to {entry.display_name}
          </span>
          <button
            className="btn-ghost"
            onClick={onClose}
            style={{ fontSize: 16, padding: "2px 8px" }}
          >
            ✕
          </button>
        </div>
        <textarea
          autoFocus
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          style={{
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            color: "var(--txt)",
            fontSize: 13,
            padding: "8px 10px",
            resize: "vertical",
            fontFamily: "inherit",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={!text.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// Track last message sent per student (entry_id → message text)
// so instructor can see what they sent without it polluting the announcement banner

function ActionMessageModal({
  title,
  description,
  placeholder,
  confirmLabel,
  confirmClass = "btn-primary",
  onConfirm,
  onClose,
}) {
  const [text, setText] = useState("");
  const handleConfirm = () => {
    onConfirm(text.trim());
    onClose();
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 24,
          width: 380,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 600, color: "var(--txt)", fontSize: 14 }}>{title}</span>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: 16, padding: "2px 8px" }}>
            ✕
          </button>
        </div>
        {description && (
          <span style={{ fontSize: 12, color: "var(--txt-dim)" }}>{description}</span>
        )}
        <textarea
          autoFocus
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          style={{
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            color: "var(--txt)",
            fontSize: 13,
            padding: "8px 10px",
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className={confirmClass} onClick={handleConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QueueCardStack({
  entries,
  isLocked,
  isPaused,
  onCall,
  onReorder,
  onMessageStudent,
  onRemove,
  onClearQueue,
  onlineStudentIds = [],
}) {
  const [messagingEntry, setMessagingEntry] = useState(null);
  const [removingEntry, setRemovingEntry] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [sentMessages, setSentMessages] = useState({}); // entry_id → message

  const handleDragEnd = (result) => {
    if (!result.destination || isLocked) return;
    if (result.destination.index === result.source.index) return;

    const reordered = Array.from(entries);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    onReorder(reordered.map((e) => e.entry_id));
  };

  return (
    <>
      {onClearQueue && entries.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 8,
          }}
        >
          <button
            className="btn-ghost"
            onClick={() => setClearing(true)}
            title="Remove all waiting students and broadcast a message"
            style={{ fontSize: 12, padding: "5px 10px", color: "var(--red, #e5484d)" }}
          >
            Clear Queue
          </button>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="queue">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`${styles.list} ${snapshot.isDraggingOver ? styles.draggingOver : ""}`}
            >
              {entries.map((entry, index) => (
                <Draggable
                  key={entry.entry_id}
                  draggableId={String(entry.entry_id)}
                  index={index}
                  isDragDisabled={isLocked}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`${styles.card} card fade-in ${snapshot.isDragging ? styles.dragging : ""}`}
                    >
                      {/* Position number */}
                      <div className={styles.position}>{index + 1}</div>

                      {/* Drag handle */}
                      {!isLocked && (
                        <div
                          className={styles.handle}
                          {...provided.dragHandleProps}
                          title="Drag to reorder"
                        >
                          ⠿
                        </div>
                      )}

                      {/* Student info */}
                      <div className={styles.info}>
                        <span className={styles.name}>
                          {entry.display_name}
                          <span
                            title={
                              onlineStudentIds.includes(entry.student_id)
                                ? "Online"
                                : "Offline — message may not be received"
                            }
                            style={{
                              display: "inline-block",
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: onlineStudentIds.includes(
                                entry.student_id,
                              )
                                ? "var(--green)"
                                : "var(--txt-faint)",
                              marginLeft: 6,
                              verticalAlign: "middle",
                              flexShrink: 0,
                            }}
                          />
                        </span>
                        {entry.pronouns && (
                          <span
                            title="Pronouns — visible to you only, not to other students"
                            style={{
                              fontSize: 11,
                              color: "var(--txt-dim)",
                              marginTop: 1,
                              display: "block",
                            }}
                          >
                            {entry.pronouns}
                          </span>
                        )}
                        <span className={styles.competency}>
                          {entry.competency_name || "Questions"}
                          {(() => {
                            // Competency call-ins carry one course; general
                            // "Questions" call-ins carry every class the student
                            // shares with this instructor. Fall back to the
                            // single course_code for older payloads.
                            const codes =
                              entry.course_codes && entry.course_codes.length
                                ? entry.course_codes
                                : entry.course_code
                                  ? [entry.course_code]
                                  : [];
                            if (!codes.length) return null;
                            return (
                              <span
                                style={{
                                  fontSize: 10,
                                  color: "var(--txt-faint)",
                                  marginLeft: 6,
                                  fontFamily: "var(--font-mono, monospace)",
                                }}
                              >
                                {codes.join(" · ")}
                              </span>
                            );
                          })()}
                        </span>
                        <span className={styles.wait}>
                          waiting {waitTime(entry.joined_at)}
                        </span>
                        {sentMessages[entry.entry_id] && (
                          <span
                            style={{
                              fontSize: 11,
                              color: "var(--gold)",
                              marginTop: 2,
                              display: "block",
                            }}
                          >
                            💬 "{sentMessages[entry.entry_id]}"
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        {/* Direct message button */}
                        {onMessageStudent && (
                          <button
                            className="btn-ghost"
                            onClick={() => setMessagingEntry(entry)}
                            title={`Send a message to ${entry.display_name}`}
                            style={{ fontSize: 14, padding: "5px 8px" }}
                          >
                            💬
                          </button>
                        )}

                        {/* Remove-from-queue button */}
                        {onRemove && (
                          <button
                            className="btn-ghost"
                            onClick={() => setRemovingEntry(entry)}
                            title={`Remove ${entry.display_name} from the queue`}
                            style={{ fontSize: 14, padding: "5px 8px", color: "var(--red, #e5484d)" }}
                          >
                            ✕
                          </button>
                        )}

                        {/* Call button */}
                        <button
                          className={`btn-primary ${styles.callBtn}`}
                          onClick={() => onCall(entry.entry_id)}
                          disabled={isPaused}
                          title={
                            isPaused ? "Queue is paused" : "Call this student"
                          }
                        >
                          Call
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Per-student message modal */}
      {messagingEntry && (
        <MessageModal
          entry={messagingEntry}
          onSend={(entryId, studentId, msg) => {
            onMessageStudent(entryId, studentId, msg);
            setSentMessages((prev) => ({ ...prev, [entryId]: msg }));
          }}
          onClose={() => setMessagingEntry(null)}
        />
      )}

      {/* Remove-single-student modal */}
      {removingEntry && (
        <ActionMessageModal
          title={`Remove ${removingEntry.display_name}?`}
          description="They'll be removed from the queue. Add an optional message they'll see explaining why."
          placeholder="Optional message to the student…"
          confirmLabel="Remove"
          confirmClass="btn-danger"
          onConfirm={(msg) => onRemove(removingEntry.entry_id, msg)}
          onClose={() => setRemovingEntry(null)}
        />
      )}

      {/* Clear-entire-queue modal */}
      {clearing && (
        <ActionMessageModal
          title="Clear the entire queue?"
          description="This removes all waiting students at once. Add an optional message everyone will see."
          placeholder="Optional broadcast message…"
          confirmLabel="Clear Queue"
          confirmClass="btn-danger"
          onConfirm={(msg) => onClearQueue(msg)}
          onClose={() => setClearing(false)}
        />
      )}
    </>
  );
}
